import { authFetch } from '../api/client';
import { fetchLocations, type ApiLocation } from './api';

const SEAWALL_API = import.meta.env.VITE_SEAWALL_API_URL ?? '/seawall';

/** Round to ~11 cm so float noise does not create duplicate sites. */
export const COORD_MATCH_DECIMALS = 6;

export type ResolvedLocation = {
  id: string;
  siteName: string;
  siteDescription: string;
  latitude: number;
  longitude: number;
  reused: boolean;
};

export type NewLocationInput = {
  latitude: number;
  longitude: number;
  siteName: string;
  siteDescription: string;
};

export function roundCoord(value: number, decimals = COORD_MATCH_DECIMALS): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

export function coordsMatch(
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number },
): boolean {
  return (
    roundCoord(a.latitude) === roundCoord(b.latitude) &&
    roundCoord(a.longitude) === roundCoord(b.longitude)
  );
}

export function toResolvedLocation(location: ApiLocation, reused: boolean): ResolvedLocation {
  return {
    id: String(location.ID),
    siteName: location.siteName,
    siteDescription: location.siteDescription,
    latitude: location.gCoord.x,
    longitude: location.gCoord.y,
    reused,
  };
}

export function findLocationByCoordinates(
  locations: ApiLocation[],
  latitude: number,
  longitude: number,
): ApiLocation | null {
  return (
    locations.find((location) =>
      coordsMatch(
        { latitude, longitude },
        { latitude: location.gCoord.x, longitude: location.gCoord.y },
      ),
    ) ?? null
  );
}

export async function loadMonitoringLocations(): Promise<ApiLocation[]> {
  return fetchLocations();
}

async function parseApiError(res: Response): Promise<string> {
  if (res.status === 401 || res.status === 403) {
    return 'Your session expired or you do not have permission. Sign in again and retry.';
  }
  try {
    const data = await res.json();
    if (typeof data?.message === 'string') return data.message;
  } catch {
    /* empty body */
  }
  return `Request failed (${res.status})`;
}

export async function createLocation(input: NewLocationInput): Promise<ResolvedLocation> {
  const body: Record<string, unknown> = {
    gCoord: { x: input.latitude, y: input.longitude },
  };
  if (input.siteName) body.siteName = input.siteName;
  if (input.siteDescription) body.siteDescription = input.siteDescription;

  const res = await authFetch(`${SEAWALL_API}/locations/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok) throw new Error(await parseApiError(res));

  const data = (await res.json()) as { id?: string | number };
  if (data.id === undefined || data.id === null) {
    throw new Error('Location was created but no ID was returned.');
  }

  return {
    id: String(data.id),
    siteName: input.siteName,
    siteDescription: input.siteDescription,
    latitude: input.latitude,
    longitude: input.longitude,
    reused: false,
  };
}

/**
 * Reuses an existing site when coordinates already exist; otherwise creates one.
 */
export async function resolveLocation(
  locations: ApiLocation[],
  input: NewLocationInput,
): Promise<ResolvedLocation> {
  const existing = findLocationByCoordinates(locations, input.latitude, input.longitude);
  if (existing) {
    return toResolvedLocation(existing, true);
  }

  return createLocation(input);
}
